import { useState, useEffect, useCallback } from "react";
import {
  getScopeTypes,
  type ScopeTypeItem,
} from "../api/accessManagement/scopeTypesService";

export interface ScopeTypeOption {
  value: string;
  label: string;
}

// Mapping from raw scope type codes to user-friendly display names.
// Keys are compared case-insensitively; fallback logic will:
// - use API description when available
// - otherwise prettify the raw type (e.g. "TOURNAMENT_MANAGER" -> "Tournament Manager")
const SCOPE_TYPE_DISPLAY_NAME_MAP: Record<string, string> = {
  tournament: "Tournament",
  tournaments: "Tournaments",
  club: "Club",
  clubs: "Clubs",
  venue: "Venue",
  venues: "Venues",
  academy: "Academy",
  academies: "Academies",
  shop: "Shop",
  shops: "Shops",
};

function getScopeTypeLabel(item: ScopeTypeItem): string {
  const rawType = item.type ?? "";
  const normalized = rawType.toLowerCase();

  if (SCOPE_TYPE_DISPLAY_NAME_MAP[normalized]) {
    return SCOPE_TYPE_DISPLAY_NAME_MAP[normalized];
  }

  if (item.description && item.description.trim().length > 0) {
    return item.description.trim();
  }

  if (!rawType) return "";

  return rawType
    .toString()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Transforms scope type items to dropdown options format.
 */
function toOptions(items: ScopeTypeItem[]): ScopeTypeOption[] {
  return items.map((item) => ({
    value: item.type,
    label: getScopeTypeLabel(item),
  }));
}

// Module-level cache to avoid duplicate API calls when multiple components use useScopeTypes
let cachedTypes: ScopeTypeItem[] | null = null;
let cachedOptions: ScopeTypeOption[] | null = null;
let fetchPromise: Promise<ScopeTypeItem[]> | null = null;

export function useScopeTypes() {
  const [scopeTypes, setScopeTypes] = useState<ScopeTypeItem[]>(cachedTypes ?? []);
  const [scopeTypeOptions, setScopeTypeOptions] = useState<ScopeTypeOption[]>(
    cachedOptions ?? []
  );
  const [loading, setLoading] = useState(!cachedTypes);
  const [error, setError] = useState<string | null>(null);

  const fetchScopeTypes = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) {
      fetchPromise = null;
      cachedTypes = null;
      cachedOptions = null;
    }
    setLoading(true);
    setError(null);
    try {
      if (fetchPromise) {
        const types = await fetchPromise;
        setScopeTypes(types);
        setScopeTypeOptions(toOptions(types));
        return;
      }
      fetchPromise = getScopeTypes();
      const types = await fetchPromise;
      cachedTypes = types;
      cachedOptions = toOptions(types);
      setScopeTypes(types);
      setScopeTypeOptions(cachedOptions);
    } catch (err) {
      fetchPromise = null;
      console.error("Failed to fetch scope types:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch scope types");
      setScopeTypes([]);
      setScopeTypeOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cachedTypes) {
      setScopeTypes(cachedTypes);
      setScopeTypeOptions(cachedOptions ?? []);
      setLoading(false);
      return;
    }
    fetchScopeTypes();
  }, [fetchScopeTypes]);

  const scopeTypeFilterOptions: ScopeTypeOption[] = [
    { value: "", label: "All" },
    ...scopeTypeOptions,
  ];

  return {
    scopeTypes,
    scopeTypeOptions,
    scopeTypeFilterOptions,
    loading,
    error,
    refetch: () => fetchScopeTypes(true),
  };
}
